/**
 * GraphQL for the self-service reseller API key card on the Manage Account page.
 *
 * Every operation is scoped server-side to the logged-in user's own verified rescue reseller — no ids
 * are sent. The plaintext key is returned only by the two mutations and is never readable again; the
 * card keeps it in component state for the one-time reveal and refetches the status query afterwards.
 */

import { gql } from '@apollo/client';

/**
 * `NONE` — never issued. `ACTIVE` — a working key exists. `REVOKED` — Fi staff revoked the key, which
 * disables self-service until customer support re-enables it.
 */
export type RescueApiKeyState = 'NONE' | 'ACTIVE' | 'REVOKED';

/** Mirrors the `RescueApiKey` GQL type — the self-service projection, without staff-only fields. */
export interface RescueApiKey {
  id: string;
  keySuffix: string;
  rateLimitReqPerMin: number;
  createdAt: string;
  lastUsedAt: string | null;
}

const rescueApiKeyFields = gql`
  fragment RescueApiKeyFields on RescueApiKey {
    id
    keySuffix
    rateLimitReqPerMin
    createdAt
    lastUsedAt
  }
`;

export const rescueApiKeyStatusQuery = gql`
  ${rescueApiKeyFields}
  query rescueApiKeyStatus {
    rescueApiKeyStatus {
      state
      apiKey {
        ...RescueApiKeyFields
      }
    }
  }
`;

export interface RescueApiKeyStatusData {
  rescueApiKeyStatus: {
    state: RescueApiKeyState;
    apiKey: RescueApiKey | null;
  };
}

export const createRescueApiKeyMutation = gql`
  ${rescueApiKeyFields}
  mutation CreateRescueApiKey {
    createRescueApiKey {
      plaintextKey
      apiKey {
        ...RescueApiKeyFields
      }
    }
  }
`;

export interface CreateRescueApiKeyData {
  createRescueApiKey: {
    plaintextKey: string;
    apiKey: RescueApiKey;
  };
}

export const regenerateRescueApiKeyMutation = gql`
  ${rescueApiKeyFields}
  mutation RegenerateRescueApiKey {
    regenerateRescueApiKey {
      plaintextKey
      apiKey {
        ...RescueApiKeyFields
      }
    }
  }
`;

export interface RegenerateRescueApiKeyData {
  regenerateRescueApiKey: {
    plaintextKey: string;
    apiKey: RescueApiKey;
  };
}
